import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form2290Schedule1 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Understanding Form 2290 Schedule 1: Proof of HVUT Payment"
          description="Comprehensive guide to understanding Form 2290 Schedule 1. Learn how to obtain your proof of HVUT payment and ensure compliance with ease."
          keywords="Form 2290 Schedule 1, 2290 stamped schedule 1, form 2290 schedule 1 efile, 2290 watermarked Schedule 1, DMV Proof of Payment"
          canonical="resources/excise-tax/form-2290-schedule-1/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 2290 -{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule 1</span>
          </span>{' '}
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 2290?
                </Typography> */}
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/excise-tax/form-2290-schedule-1/"
                      />

                      {/* Article requires an image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-08-21T09:00:00-05:00"
                          >
                            August 21, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-08-21T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            August 21, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Alexia Zepeda
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          color: '#01456c',
                          fontStyle: 'italic',
                        }}
                      >
                        Form 2290 Schedule 1 at a Glance
                      </Typography>

                      <Box
                        component="ul"
                        itemScope
                        itemType="https://schema.org/ItemList"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="1"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              What is Schedule 1:
                            </strong>{' '}
                            IRS-stamped Schedule 1 serves as{' '}
                            <strong style={{ fontWeight: 600 }}>
                              proof of Heavy Vehicle Use Tax (HVUT) payment
                            </strong>{' '}
                            after you file Form 2290 and pay the tax.
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="2"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Validity:
                            </strong>{' '}
                            Schedule 1 generally corresponds to the{' '}
                            <strong style={{ fontWeight: 600 }}>
                              July 1–June 30 HVUT tax period
                            </strong>{' '}
                            for which the tax was filed.
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="3"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Why It Matters:
                            </strong>{' '}
                            It provides evidence of HVUT compliance and is
                            commonly required for{' '}
                            <strong style={{ fontWeight: 600 }}>
                              vehicle registration and renewal.
                            </strong>
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="4"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Department of Motor Vehicles (DMV) or Department
                              of Transportation (DOT):
                            </strong>{' '}
                            Many state authorities require a valid Schedule 1 as
                            proof of HVUT payment before{' '}
                            <strong style={{ fontWeight: 600 }}>
                              registering or renewing
                            </strong>{' '}
                            a heavy vehicle.
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="5"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Inspections:
                            </strong>{' '}
                            Keeping a copy of Schedule 1 can help demonstrate{' '}
                            <strong style={{ fontWeight: 600 }}>
                              HVUT compliance during applicable inspections.
                            </strong>
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>

                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                      If you&apos;re a trucker or owner of a heavy vehicle,
                      you&apos;re probably familiar with{' '}
                      <Link
                        href="/resources/excise-tax/form-2290-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        2290 tax form
                      </Link>{' '}
                      which is used to report and pay the heavy vehicle use tax
                      (HVUT). But did you know that once you&apos;ve filed your
                      IRS Form 2290 and paid your HVUT, you also need to obtain
                      a stamped Schedule 1 to prove that you&apos;ve paid the
                      tax?
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      In this article, we&apos;ll learn about the{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        IRS Form 2290
                      </Link>{' '}
                      stamped Schedule 1 and provide an overview of its
                      importance.
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 2290 Schedule 1?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why is Schedule 1 Important?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How is Schedule 1 used as Proof for DMV and DOT
                          Renewal?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#get" sx={{ color: '#0202ff' }}>
                          E-file Form 2290 and get your Stamped Schedule 1
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What is Form 2290 Schedule 1?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 2290 Schedule 1 is proof of payment issued by the IRS
                  that you have paid the heavy vehicle use tax (HVUT).
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule 1 is typically valid from July 1st of the current
                  year to June 30th of the following year, corresponding to the
                  tax period for which the HVUT is paid. So, it&apos;s necessary
                  to file your HVUT Form 2290 on time and accurately pay your
                  HVUT payment to receive your stamped Schedule 1 before the
                  current period expires.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Once the IRS processes your{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    HVUT Form 2290
                  </Link>{' '}
                  and verifies that the required HVUT has been paid, they will
                  stamp and return Schedule 1 to the taxpayer as proof of
                  payment. This stamp indicates that the tax amount has been
                  paid and the vehicle is legally authorized to operate on
                  public highways.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="why"
                >
                  Why is Schedule 1 Important?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Obtaining your watermarked Schedule 1 is a crucial part of
                  your{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 2290
                  </Link>{' '}
                  filing, as it serves as proof of payment for the HVUT that you
                  paid with the IRS. It is also required by the Department of
                  Motor Vehicles (DMV) to register new vehicles or renew the
                  registration of existing vehicles.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Simply put, vehicle owners may not be able to legally operate
                  their vehicles on public highways without form 2290 schedule
                  1.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Below are a few more things that show the importance of having
                  a stamped Schedule 1:
                </Typography>

                <ul>
                  <li>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <b>Proof of Payment:</b> Schedule 1 acts as a receipt,
                      proving that you have paid the required HVUT. This proof
                      of payment you received from the IRS is valuable as it
                      acts as evidence of your compliance with tax obligations.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <b>Vehicle Registration and Renewal:</b> Schedule 1 is
                      often required during the registration or renewal process
                      with the Department of Motor Vehicles (DMV). DMVs across
                      the country typically request a copy of the stamped
                      Schedule 1 as proof of tax payment before they can issue
                      or renew vehicle registrations.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Truckers also use schedule 1 2290 tax form to register
                      their vehicles at DMVs. Therefore, this ensures that only
                      vehicles that have met their tax obligations and are
                      legally registered are allowed to operate on public
                      highways.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <b>Interstate Travel Inspections:</b> When operating heavy
                      vehicles on public highways or interstate, you may
                      encounter inspections conducted by law enforcement
                      agencies or state authorities. These inspections aim to
                      ensure compliance with various state regulations,
                      including{' '}
                      <Link
                        href="/resources/excise-tax/form-2290-irs-payment-options/"
                        sx={{ color: '#0202ff' }}
                      >
                        HVUT payment
                      </Link>
                      .
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      During such inspections, the stamped Schedule 1 is
                      considered a vital document that verifies your compliance
                      with the HVUT, and its absence can result in penalties or
                      travel restrictions.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 5 }}>
                      <b>Compliance with IRS Regulations:</b> Schedule 1 acts as
                      evidence of compliance with state and federal tax
                      requirements. It demonstrates that the necessary taxes
                      have been paid, ensuring adherence to IRS guidelines and
                      avoiding{' '}
                      <Link
                        href="/resources/excise-tax/hvut-form-2290-penalties/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 2290 late penalties
                      </Link>
                      .
                    </Typography>
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule 1 is an important document for all heavy vehicle
                  owners and operators who are required to file Form 2290 and
                  pay their HVUT. Obtaining a valid Schedule 1 not only serves
                  as proof of HVUT payment but also makes you compliant with IRS
                  regulations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  By understanding Schedule 1 and its importance, heavy vehicle
                  owners can meet their tax obligations, comply with IRS
                  regulations, and can operate on public highways with peace of
                  mind.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="how"
                >
                  How is Schedule 1 used as Proof for DMV and DOT Renewal?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When owners or operators of heavy vehicles need to renew their
                  Department of Motor Vehicles (DMV) or Department of
                  Transportation (DOT) registrations or permits, a valid
                  Schedule 1 serves as proof of HVUT payment. Many state
                  authorities require this proof to ensure the required HVUT
                  taxes have been paid before granting renewal privileges.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Without a valid Schedule 1, the DMV or DOT may withhold
                  vehicle renewals or registrations until the HVUT tax liability
                  is satisfied.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="get"
                >
                  E-file Form 2290 and get your Stamped Schedule 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS recommends{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-filing Form 2290
                  </Link>{' '}
                  over paper filing as the process is faster and you get
                  immediate access to your Schedule 1.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  By filing your Form 2290 online using an IRS-authorized e-file
                  provider like TaxZerone, you will receive your stamped
                  Schedule 1 in just a few minutes! With our user-friendly
                  approach and step-by-step filing process, we ensure that your
                  Form 2290 is transmitted to the IRS error-free!
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to Get Your Stamped Schedule 1 Fast?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Don’t wait in long lines or deal with paperwork delays. E-file
              <span style={{ fontWeight: 600 }}>
                {' '}
                Form 2290 with TaxZerone
              </span>{' '}
              <br />
              and get your{' '}
              <span style={{ fontWeight: 600 }}>
                IRS-stamped Schedule 1 in minutes
              </span>{' '}
              — quick, secure, and hassle-free.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=sch1`}
              >
                E-file now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form2290Schedule1;
